import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TasksAiEditor } from '@/ai-editor';
import { ImageUpload } from './ImageUpload';
import { VideoUpload } from './VideoUpload';
import { FileUpload } from './FileUpload';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  X,
  ImageIcon,
  Video,
  FileText,
  Table,
  Code,
  GitBranch,
  Save,
  MoreHorizontal,
  Trash2,
  GripVertical,
  Plus,
  Link as LinkIcon,
  Download,
  File as FileIcon,
  Globe,
  Eye,
  Pencil,
  Lock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDraggable, useDroppable } from '@dnd-kit/core';

// Embed Block Component
function EmbedBlock({ data, onChange }) {
  const [url, setUrl] = React.useState(data.url || '');
  const [inputUrl, setInputUrl] = React.useState(data.url || '');
  const [isEditing, setIsEditing] = React.useState(!data.url);

  const handleSubmit = () => {
    if (inputUrl.trim()) {
      setUrl(inputUrl.trim());
      onChange({ url: inputUrl.trim() });
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (!url || isEditing) {
    return (
      <div className="border-2 border-primary/20 rounded-lg p-8 bg-muted/5">
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <LinkIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <Input
            type="url"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type or paste website embed URL here"
            className="flex-1"
            autoFocus
          />
          <Button onClick={handleSubmit} disabled={!inputUrl.trim()}>
            Embed
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-muted/5">
      <div className="flex items-center justify-between px-4 py-2 bg-muted/30 border-b">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <LinkIcon className="h-4 w-4" />
          <span className="truncate max-w-md">{url}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
        >
          Edit URL
        </Button>
      </div>
      <div className="relative w-full h-[600px] bg-white">
        <iframe
          src={url}
          className="w-full h-full border-0"
          title="Embedded content"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          loading="lazy"
        />
      </div>
    </div>
  );
}

// Table Block Component - Word-like functionality
function TableBlock({ data, onChange }) {
  const [rows, setRows] = React.useState(data.rows || 3);
  const [cols, setCols] = React.useState(data.cols || 3);
  const [cells, setCells] = React.useState(data.cells || {});
  const [selectedCell, setSelectedCell] = React.useState(null);
  const [contextMenu, setContextMenu] = React.useState(null);
  const tableRef = React.useRef(null);

  React.useEffect(() => {
    onChange({ rows, cols, cells });
  }, [rows, cols, cells]);

  // Close context menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const getCellKey = (row, col) => `${row}-${col}`;

  const updateCell = (row, col, value) => {
    const newCells = { ...cells };
    newCells[getCellKey(row, col)] = value;
    setCells(newCells);
  };

  const insertRowAbove = (rowIndex) => {
    const newCells = { ...cells };
    // Shift all rows below down
    for (let row = rows - 1; row >= rowIndex; row--) {
      for (let col = 0; col < cols; col++) {
        const oldKey = getCellKey(row, col);
        const newKey = getCellKey(row + 1, col);
        if (newCells[oldKey]) {
          newCells[newKey] = newCells[oldKey];
          delete newCells[oldKey];
        }
      }
    }
    setCells(newCells);
    setRows(rows + 1);
  };

  const insertRowBelow = (rowIndex) => {
    const newCells = { ...cells };
    // Shift all rows below down
    for (let row = rows - 1; row > rowIndex; row--) {
      for (let col = 0; col < cols; col++) {
        const oldKey = getCellKey(row, col);
        const newKey = getCellKey(row + 1, col);
        if (newCells[oldKey]) {
          newCells[newKey] = newCells[oldKey];
          delete newCells[oldKey];
        }
      }
    }
    setCells(newCells);
    setRows(rows + 1);
  };

  const insertColumnLeft = (colIndex) => {
    const newCells = { ...cells };
    // Shift all columns to the right
    for (let row = 0; row < rows; row++) {
      for (let col = cols - 1; col >= colIndex; col--) {
        const oldKey = getCellKey(row, col);
        const newKey = getCellKey(row, col + 1);
        if (newCells[oldKey]) {
          newCells[newKey] = newCells[oldKey];
          delete newCells[oldKey];
        }
      }
    }
    setCells(newCells);
    setCols(cols + 1);
  };

  const insertColumnRight = (colIndex) => {
    const newCells = { ...cells };
    // Shift all columns to the right
    for (let row = 0; row < rows; row++) {
      for (let col = cols - 1; col > colIndex; col--) {
        const oldKey = getCellKey(row, col);
        const newKey = getCellKey(row, col + 1);
        if (newCells[oldKey]) {
          newCells[newKey] = newCells[oldKey];
          delete newCells[oldKey];
        }
      }
    }
    setCells(newCells);
    setCols(cols + 1);
  };

  const deleteRow = (rowIndex) => {
    if (rows <= 1) return;
    const newCells = { ...cells };
    // Remove cells from deleted row
    for (let col = 0; col < cols; col++) {
      delete newCells[getCellKey(rowIndex, col)];
    }
    // Shift cells from rows below up
    for (let row = rowIndex + 1; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const oldKey = getCellKey(row, col);
        const newKey = getCellKey(row - 1, col);
        if (newCells[oldKey]) {
          newCells[newKey] = newCells[oldKey];
          delete newCells[oldKey];
        }
      }
    }
    setCells(newCells);
    setRows(rows - 1);
    setContextMenu(null);
  };

  const deleteColumn = (colIndex) => {
    if (cols <= 1) return;
    const newCells = { ...cells };
    // Remove cells from deleted column
    for (let row = 0; row < rows; row++) {
      delete newCells[getCellKey(row, colIndex)];
    }
    // Shift cells from columns to the right
    for (let row = 0; row < rows; row++) {
      for (let col = colIndex + 1; col < cols; col++) {
        const oldKey = getCellKey(row, col);
        const newKey = getCellKey(row, col - 1);
        if (newCells[oldKey]) {
          newCells[newKey] = newCells[oldKey];
          delete newCells[oldKey];
        }
      }
    }
    setCells(newCells);
    setCols(cols - 1);
    setContextMenu(null);
  };

  const handleCellContextMenu = (e, rowIndex, colIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      rowIndex,
      colIndex,
    });
  };

  const handleCellKeyDown = (e, rowIndex, colIndex) => {
    // Tab: Move to next cell (or first cell of next row)
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const nextCol = colIndex + 1;
      if (nextCol < cols) {
        const nextInput = tableRef.current?.querySelector(`input[data-row="${rowIndex}"][data-col="${nextCol}"]`);
        nextInput?.focus();
      } else if (rowIndex + 1 < rows) {
        const nextInput = tableRef.current?.querySelector(`input[data-row="${rowIndex + 1}"][data-col="0"]`);
        nextInput?.focus();
      }
    }
    // Shift+Tab: Move to previous cell
    else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      const prevCol = colIndex - 1;
      if (prevCol >= 0) {
        const prevInput = tableRef.current?.querySelector(`input[data-row="${rowIndex}"][data-col="${prevCol}"]`);
        prevInput?.focus();
      } else if (rowIndex > 0) {
        const prevInput = tableRef.current?.querySelector(`input[data-row="${rowIndex - 1}"][data-col="${cols - 1}"]`);
        prevInput?.focus();
      }
    }
  };

  return (
    <div className="relative" ref={tableRef}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <tbody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: cols }).map((_, colIndex) => {
                  const cellKey = getCellKey(rowIndex, colIndex);
                  const isSelected = selectedCell === cellKey;

                  return (
                    <td
                      key={colIndex}
                      className={`border border-gray-300 p-0 min-w-[120px] relative ${
                        isSelected ? 'ring-2 ring-primary ring-inset' : ''
                      } ${rowIndex === 0 ? 'bg-muted/30' : ''}`}
                      onContextMenu={(e) => handleCellContextMenu(e, rowIndex, colIndex)}
                    >
                      <input
                        type="text"
                        data-row={rowIndex}
                        data-col={colIndex}
                        value={cells[cellKey] || ''}
                        onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                        onFocus={() => setSelectedCell(cellKey)}
                        onBlur={() => setSelectedCell(null)}
                        onKeyDown={(e) => handleCellKeyDown(e, rowIndex, colIndex)}
                        className={`w-full h-full px-2 py-2 bg-transparent outline-none ${
                          rowIndex === 0 ? 'font-medium' : ''
                        }`}
                        placeholder={rowIndex === 0 ? 'Header' : ''}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-white border shadow-lg rounded-md py-1 z-50 min-w-[180px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
            onClick={() => insertRowAbove(contextMenu.rowIndex)}
          >
            Insert Row Above
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
            onClick={() => insertRowBelow(contextMenu.rowIndex)}
          >
            Insert Row Below
          </button>
          <div className="border-t my-1" />
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
            onClick={() => insertColumnLeft(contextMenu.colIndex)}
          >
            Insert Column Left
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
            onClick={() => insertColumnRight(contextMenu.colIndex)}
          >
            Insert Column Right
          </button>
          <div className="border-t my-1" />
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-muted text-destructive flex items-center gap-2"
            onClick={() => deleteRow(contextMenu.rowIndex)}
            disabled={rows <= 1}
          >
            Delete Row
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-muted text-destructive flex items-center gap-2"
            onClick={() => deleteColumn(contextMenu.colIndex)}
            disabled={cols <= 1}
          >
            Delete Column
          </button>
        </div>
      )}
    </div>
  );
}

// Draggable sidebar item
function DraggableSidebarItem({ item }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `draggable-${item.type}`,
    data: {
      type: 'new-block',
      blockType: item.type,
    },
  });

  const Icon = item.icon;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing hover:bg-muted/50 transition-colors ${
        isDragging ? 'opacity-30' : ''
      }`}
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm font-medium">{item.label}</span>
    </div>
  );
}

// Sortable content block
function SortableContentBlock({ block, onRemove, onUpdate, projectId }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const renderContent = () => {
    switch (block.type) {
      case 'text':
        return (
          <div>
            <style>{`
              .text-editor-wrapper .tiptap-toolbar,
              .text-editor-wrapper [role="toolbar"],
              .text-editor-wrapper .editor-toolbar,
              .text-editor-wrapper .formatting-toolbar,
              .text-editor-wrapper > div:first-child > div:first-child {
                opacity: 0;
                transition: opacity 0.2s;
                pointer-events: none;
              }
              .text-editor-wrapper:hover .tiptap-toolbar,
              .text-editor-wrapper:hover [role="toolbar"],
              .text-editor-wrapper:hover .editor-toolbar,
              .text-editor-wrapper:hover .formatting-toolbar,
              .text-editor-wrapper:hover > div:first-child > div:first-child,
              .text-editor-wrapper .tiptap-toolbar:hover,
              .text-editor-wrapper [role="toolbar"]:hover,
              .text-editor-wrapper .editor-toolbar:hover,
              .text-editor-wrapper .formatting-toolbar:hover,
              .text-editor-wrapper > div:first-child > div:first-child:hover {
                opacity: 1;
                pointer-events: auto;
              }
              .text-editor-wrapper .ProseMirror,
              .text-editor-wrapper .tiptap,
              .text-editor-wrapper > div {
                border: 0 !important;
                min-height: auto !important;
                padding: 0 !important;
              }
              .text-editor-wrapper .ProseMirror {
                min-height: 1.5em !important;
              }
              .text-editor-wrapper .ProseMirror p.is-editor-empty:first-child::before {
                color: #9ca3af;
                content: attr(data-placeholder);
                float: left;
                height: 0;
                pointer-events: none;
              }
            `}</style>
            <div className="text-editor-wrapper">
              <TasksAiEditor
                value={block.content || ''}
                onChange={(html) => onUpdate(block.id, { content: html })}
                placeholder="Write something"
              />
            </div>
          </div>
        );
      case 'image':
        return (
          <div>
            {block.content ? (
              <img src={block.content} alt="" className="max-w-full rounded-lg" />
            ) : (
              <ImageUpload
                onImageSelected={(url) => onUpdate(block.id, { content: url })}
                currentImage={null}
                folderPath={projectId ? `spaces/${projectId}/pages/${block.id}` : `pages/${block.id}`}
              />
            )}
          </div>
        );
      case 'video':
        // If video is already uploaded, show it with change button
        if (block.content) {
          const isEmbedUrl = (url) => {
            return url.includes('youtube.com/embed') ||
                   url.includes('youtu.be') ||
                   url.includes('vimeo.com');
          };

          return (
            <div className="relative group/media">
              {isEmbedUrl(block.content) ? (
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={block.content}
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video content"
                  />
                </div>
              ) : (
                <video
                  src={block.content}
                  controls
                  className="w-full h-auto rounded-lg"
                >
                  Your browser does not support the video tag.
                </video>
              )}
              <Button
                variant="secondary"
                size="sm"
                className="absolute top-2 right-2 opacity-0 group-hover/media:opacity-100 transition-opacity z-10"
                onClick={() => onUpdate(block.id, { content: null })}
              >
                Change Video
              </Button>
            </div>
          );
        }
        // Show upload interface if no video
        return (
          <VideoUpload
            currentVideo={null}
            onVideoSelected={(url) => onUpdate(block.id, { content: url })}
            folderPath={projectId ? `spaces/${projectId}/pages/${block.id}` : `pages/${block.id}`}
          />
        );
      case 'file':
        // If file is already uploaded, show it with download option
        if (block.content) {
          const fileData = typeof block.content === 'string'
            ? { url: block.content, name: block.filename || 'File', size: null }
            : block.content;

          const formatFileSize = (bytes) => {
            if (!bytes) return '';
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
          };

          const handleDownload = () => {
            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = fileData.url;
            link.download = fileData.name || 'download';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          };

          return (
            <div className="space-y-3">
              <div className="border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center gap-3">
                  <FileIcon className="h-8 w-8 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{fileData.name}</div>
                    {fileData.size && (
                      <div className="text-xs text-muted-foreground">
                        {formatFileSize(fileData.size)}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleDownload}
                    title="Download file"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => onUpdate(block.id, { content: null, filename: null })}
              >
                Change File
              </Button>
            </div>
          );
        }
        // Show upload interface if no file
        return (
          <FileUpload
            currentFile={null}
            onFileSelected={(fileData) => onUpdate(block.id, { content: fileData.url, filename: fileData.name })}
            folderPath={projectId ? `spaces/${projectId}/pages/${block.id}` : `pages/${block.id}`}
          />
        );
      case 'table':
        return (
          <TableBlock
            data={block.content || { rows: 3, cols: 3, cells: {} }}
            onChange={(tableData) => onUpdate(block.id, { content: tableData })}
          />
        );
      case 'embed':
        return (
          <EmbedBlock
            data={block.content || {}}
            onChange={(embedData) => onUpdate(block.id, { content: embedData })}
          />
        );
      case 'workflow':
        return <div className="border rounded p-4 text-muted-foreground">Workflow block (coming soon)</div>;
      default:
        return null;
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="group relative mb-4">
      <div className="flex gap-2">
        <div
          {...attributes}
          {...listeners}
          className="flex items-start pt-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1">
          {renderContent()}
        </div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onRemove(block.id)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

// Drop zone indicator - shows as a line when hovering
function DropZoneIndicator({ isOver }) {
  if (!isOver) return null;

  return (
    <div className="h-1 bg-primary rounded-full my-2 transition-all duration-200" />
  );
}

export function PageEditorPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get('id');
  const { userProfile, company } = useAuth();

  // Set page title
  usePageTitle('Page Editor');

  const [title, setTitle] = useState('Untitled');
  const [coverImage, setCoverImage] = useState(null);
  const [showCoverUpload, setShowCoverUpload] = useState(false);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [activeDragItem, setActiveDragItem] = useState(null);
  const [currentPageId, setCurrentPageId] = useState(pageId);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [isLoadingPage, setIsLoadingPage] = useState(!!pageId);
  const [pageStatus, setPageStatus] = useState('draft'); // draft, published
  const [pageCreatedBy, setPageCreatedBy] = useState(null);

  // Track original values to detect changes (for auto-unpublish)
  const [originalTitle, setOriginalTitle] = useState('Untitled');
  const [originalCoverImage, setOriginalCoverImage] = useState(null);
  const [originalContentBlocks, setOriginalContentBlocks] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Check if current user is the author
  const isAuthor = !pageCreatedBy || pageCreatedBy === userProfile?.id;

  // Check if content has changed from original (for published pages)
  const hasChanges = pageStatus === 'published' && (
    title !== originalTitle ||
    coverImage !== originalCoverImage ||
    JSON.stringify(contentBlocks) !== JSON.stringify(originalContentBlocks)
  );

  // Get space data if in a space context
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    })
  );

  // Sidebar content types
  const sidebarItems = [
    { type: 'text', label: 'Text', icon: FileText },
    { type: 'image', label: 'Image', icon: ImageIcon },
    { type: 'video', label: 'Video', icon: Video },
    { type: 'file', label: 'File', icon: FileText },
    { type: 'table', label: 'Table', icon: Table },
    { type: 'embed', label: 'Embed', icon: Code },
    { type: 'workflow', label: 'Workflows', icon: GitBranch },
  ];

  const handleCoverSelected = (imageUrl) => {
    setCoverImage(imageUrl);
    setShowCoverUpload(false);
  };

  const handleRemoveCover = () => {
    setCoverImage(null);
  };

  const handleDragStart = (event) => {
    setActiveDragItem(event.active.data.current);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragItem(null);

    if (!over) return;

    // Adding new block from sidebar
    if (active.data.current?.type === 'new-block') {
      const newBlock = {
        id: `block-${Date.now()}`,
        type: active.data.current.blockType,
        content: '',
      };

      // If dropped on a specific position
      if (over.id.toString().startsWith('drop-zone-')) {
        const index = parseInt(over.id.toString().replace('drop-zone-', ''));
        const newBlocks = [...contentBlocks];
        newBlocks.splice(index, 0, newBlock);
        setContentBlocks(newBlocks);
      } else {
        // Add to end
        setContentBlocks([...contentBlocks, newBlock]);
      }
    }
    // Reordering existing blocks
    else if (active.id !== over.id) {
      setContentBlocks((blocks) => {
        const oldIndex = blocks.findIndex((b) => b.id === active.id);
        const newIndex = blocks.findIndex((b) => b.id === over.id);
        return arrayMove(blocks, oldIndex, newIndex);
      });
    }
  };

  const handleRemoveBlock = (blockId) => {
    setContentBlocks(contentBlocks.filter((b) => b.id !== blockId));
  };

  const handleUpdateBlock = (blockId, updates) => {
    setContentBlocks(
      contentBlocks.map((b) => (b.id === blockId ? { ...b, ...updates } : b))
    );
  };

  // Load existing page
  useEffect(() => {
    const loadPage = async () => {
      if (!pageId) {
        setIsLoadingPage(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('pages')
          .select('*')
          .eq('id', pageId)
          .single();

        if (error) throw error;

        if (data) {
          setTitle(data.name || 'Untitled');
          setCoverImage(data.cover_image);
          setContentBlocks(data.content_blocks || []);
          setCurrentPageId(data.id);
          setPageStatus(data.status || 'draft');
          setPageCreatedBy(data.created_by);

          // If page is published and user is not author, redirect to viewer
          if (data.status === 'published' && data.created_by !== userProfile?.id) {
            toast.info('This page is published. Only the author can edit it.');
            navigate(`/space/${projectId}/page/${data.id}`);
            return;
          }

          // Store original values to detect changes
          setOriginalTitle(data.name || 'Untitled');
          setOriginalCoverImage(data.cover_image);
          setOriginalContentBlocks(data.content_blocks || []);
        }
      } catch (error) {
        console.error('Error loading page:', error);
        toast.error('Failed to load page');
      } finally {
        setIsLoadingPage(false);
      }
    };

    loadPage();
  }, [pageId]);

  // Save page to database (keeps status, doesn't change draft/published)
  const savePage = useCallback(async (newStatus = null) => {
    if (!projectId || !userProfile?.id) return;

    setSaveStatus('saving');

    try {
      // Determine status: use newStatus if provided, otherwise keep current
      // If page was published and has changes, auto-unpublish to draft
      let statusToSave = newStatus || pageStatus;
      if (pageStatus === 'published' && hasChanges && !newStatus) {
        statusToSave = 'draft';
        setPageStatus('draft');
      }

      const pageData = {
        name: title,
        project_id: projectId,
        cover_image: coverImage,
        content_blocks: contentBlocks,
        status: statusToSave,
      };

      if (currentPageId) {
        // Update existing page
        const { error } = await supabase
          .from('pages')
          .update(pageData)
          .eq('id', currentPageId);

        if (error) throw error;
      } else {
        // Create new page
        const { data, error } = await supabase
          .from('pages')
          .insert({
            ...pageData,
            created_by: userProfile.id,
          })
          .select()
          .single();

        if (error) throw error;

        if (data) {
          setCurrentPageId(data.id);
          setPageCreatedBy(data.created_by);
          // Update URL with page ID
          const newPath = projectId
            ? `/space/${projectId}/page-editor?id=${data.id}`
            : `/tasks/page-editor?id=${data.id}`;
          navigate(newPath, { replace: true });
        }
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving page:', error);
      setSaveStatus('error');
      toast.error('Failed to save page');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [projectId, userProfile, title, coverImage, contentBlocks, currentPageId, navigate, pageStatus, hasChanges]);

  // Publish page (saves current content and publishes)
  const publishPage = useCallback(async () => {
    if (!projectId || !userProfile?.id) return;

    setSaveStatus('saving');

    try {
      const pageData = {
        name: title,
        project_id: projectId,
        cover_image: coverImage,
        content_blocks: contentBlocks,
        status: 'published',
      };

      if (currentPageId) {
        // Update existing page
        const { error } = await supabase
          .from('pages')
          .update(pageData)
          .eq('id', currentPageId);

        if (error) throw error;
      } else {
        // Create new page
        const { data, error } = await supabase
          .from('pages')
          .insert({
            ...pageData,
            created_by: userProfile.id,
          })
          .select()
          .single();

        if (error) throw error;

        if (data) {
          setCurrentPageId(data.id);
          setPageCreatedBy(data.created_by);
          const newPath = `/space/${projectId}/page-editor?id=${data.id}`;
          navigate(newPath, { replace: true });
        }
      }

      // Update original values to current (no more "changes" after publish)
      setOriginalTitle(title);
      setOriginalCoverImage(coverImage);
      setOriginalContentBlocks(contentBlocks);

      setPageStatus('published');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      toast.success('Page published successfully!');
    } catch (error) {
      console.error('Error publishing page:', error);
      setSaveStatus('error');
      toast.error('Failed to publish page');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [projectId, userProfile, title, coverImage, contentBlocks, currentPageId, navigate]);

  // Unpublish page (revert to draft)
  const unpublishPage = useCallback(async () => {
    if (!currentPageId) return;

    try {
      const { error } = await supabase
        .from('pages')
        .update({ status: 'draft' })
        .eq('id', currentPageId);

      if (error) throw error;

      setPageStatus('draft');
      toast.success('Page reverted to draft');
    } catch (error) {
      console.error('Error unpublishing page:', error);
      toast.error('Failed to unpublish page');
    }
  }, [currentPageId]);

  // View published page
  const viewPage = () => {
    if (currentPageId) {
      navigate(`/space/${projectId}/page/${currentPageId}`);
    }
  };

  // Auto-save when data changes (debounced)
  useEffect(() => {
    if (isLoadingPage) return;

    const timeoutId = setTimeout(() => {
      savePage();
    }, 1000); // Save 1 second after last change

    return () => clearTimeout(timeoutId);
  }, [title, coverImage, contentBlocks, savePage, isLoadingPage]);

  return (
    <AppShell3 pageTitle="">
      <PageTransition>
        <ToolHeader
          spaceName={space?.name || 'Pages'}
          spaceImage={space?.logo_url}
          toolName={title}
          currentTool='tasks'
          spaceTools={spaceTools}
          parentBreadcrumb={{
            name: 'Tasks',
            onClick: () => navigate(`/space/${projectId}/tasks`)
          }}
          actions={
            <div className="flex items-center gap-3">
              {/* Status indicator */}
              {pageStatus === 'published' && !hasChanges && (
                <div className="flex items-center gap-1.5 text-green-600 text-sm">
                  <Lock className="h-4 w-4" />
                  <span>Published</span>
                </div>
              )}
              {hasChanges && (
                <div className="flex items-center gap-1.5 text-amber-600 text-sm">
                  <Pencil className="h-4 w-4" />
                  <span>Unsaved changes</span>
                </div>
              )}

              {/* View button - only show when published and no changes */}
              {pageStatus === 'published' && !hasChanges && currentPageId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={viewPage}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View
                </Button>
              )}

              {/* Save button - hide when published without changes (auto-save handles it) */}
              {(pageStatus === 'draft' || hasChanges) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => savePage()}
                  disabled={saveStatus === 'saving'}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save'}
                </Button>
              )}

              {/* Publish button - for author */}
              {isAuthor && (
                pageStatus === 'draft' || hasChanges ? (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={publishPage}
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    {hasChanges ? 'Publish Changes' : 'Publish'}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={unpublishPage}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Unpublish
                  </Button>
                )
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    Share
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive">
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          }
        />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="min-h-screen bg-gray-100 relative">
            {/* Floating Sticky Sidebar - positioned at the top of the content overlap */}
            <div className="fixed right-6 w-[200px] z-50" style={{ top: coverImage ? '320px' : '150px' }}>
              <div className="bg-white border rounded-xl shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b bg-muted/30">
                  <span className="text-xs font-semibold text-muted-foreground tracking-wide">CONTENT</span>
                </div>

                <div className="p-2">
                  {sidebarItems.map((item) => (
                    <DraggableSidebarItem key={item.type} item={item} />
                  ))}
                </div>
              </div>
            </div>

        {/* Main Content Area - Full Width */}
        <div>
          {/* Cover Image Section - Full Width */}
          {coverImage && (
            <div className="relative w-full h-80 mb-0 group">
              <img
                src={coverImage}
                alt="Cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRemoveCover}
                  className="shadow-lg"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              </div>
            </div>
          )}

          <div className={`max-w-4xl mx-auto px-24 bg-white shadow-sm ${coverImage ? 'rounded-t-3xl -mt-16 relative z-10 pt-16 pb-12' : 'rounded-xl mt-6 py-12'}`}>
            {/* Upload Section or Add Cover Button */}
            {!coverImage && showCoverUpload ? (
              <div className="mb-6">
                <ImageUpload
                  onImageSelected={handleCoverSelected}
                  currentImage={null}
                  folderPath={projectId ? `spaces/${projectId}/pages/${pageId}/cover` : `pages/${pageId}/cover`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCoverUpload(false)}
                  className="mt-2"
                >
                  Cancel
                </Button>
              </div>
            ) : !coverImage && (
              <button
                className="text-sm text-muted-foreground hover:text-foreground mb-6 flex items-center gap-2"
                onClick={() => setShowCoverUpload(true)}
              >
                <ImageIcon className="h-4 w-4" />
                Add cover
              </button>
            )}

            {/* Title */}
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled"
              className="text-5xl font-bold border-0 px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 mb-8 h-auto"
              style={{ fontSize: '3rem', lineHeight: '1.2' }}
            />

            {/* Content Blocks */}
            <div className="mt-4 group">
              {contentBlocks.length === 0 ? (
                <DroppableZone id="drop-zone-0">
                  {(isOver) => (
                    <div
                      className={`min-h-[200px] flex items-center justify-center rounded-lg border-2 border-dashed transition-all duration-200 ${
                        isOver
                          ? 'border-primary bg-primary/5 scale-105'
                          : 'border-muted/40 bg-muted/10'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Plus className="h-8 w-8" />
                        <p className="text-sm">Drag content blocks here to start building your page</p>
                      </div>
                    </div>
                  )}
                </DroppableZone>
              ) : (
                <SortableContext items={contentBlocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                  {contentBlocks.map((block, index) => (
                    <React.Fragment key={block.id}>
                      {/* Only show drop zone between blocks when dragging */}
                      {activeDragItem && (
                        <DroppableZone id={`drop-zone-${index}`}>
                          {(isOver) => <DropZoneIndicator isOver={isOver} />}
                        </DroppableZone>
                      )}
                      <SortableContentBlock
                        block={block}
                        onRemove={handleRemoveBlock}
                        onUpdate={handleUpdateBlock}
                        projectId={projectId}
                      />
                    </React.Fragment>
                  ))}
                  {/* Always show drop zone at the bottom */}
                  <DroppableZone id={`drop-zone-${contentBlocks.length}`}>
                    {(isOver) => (
                      isOver ? (
                        <DropZoneIndicator isOver={true} />
                      ) : activeDragItem ? (
                        <div className="h-1 bg-muted/40 rounded-full my-2" />
                      ) : (
                        <div className="mt-2 pt-4 border-t border-dashed border-muted/40 flex items-center justify-center text-muted-foreground text-sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Drag blocks here
                        </div>
                      )
                    )}
                  </DroppableZone>
                </SortableContext>
              )}
            </div>
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeDragItem && activeDragItem.type === 'new-block' && (
          <div className="bg-white shadow-lg rounded-lg p-4 border-2 border-primary opacity-80">
            <div className="flex items-center gap-2">
              {activeDragItem.blockType && (
                <>
                  {sidebarItems.find(i => i.type === activeDragItem.blockType)?.icon &&
                    React.createElement(sidebarItems.find(i => i.type === activeDragItem.blockType).icon, {
                      className: "h-5 w-5"
                    })
                  }
                  <span className="font-medium">{activeDragItem.blockType}</span>
                </>
              )}
            </div>
          </div>
        )}
      </DragOverlay>
        </DndContext>
      </PageTransition>
    </AppShell3>
  );
}

// Droppable zone component
function DroppableZone({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return <div ref={setNodeRef}>{children(isOver)}</div>;
}
